/**
 * unmute-ios-audio.
 *
 * iOS-Safari silent-switch workaround. The hardware mute switch silences Web Audio
 * unless an HTMLAudioElement is actively playing a looped MP3 *inside the same user
 * gesture* that started the AudioContext. We start a looped silent MP3 element on the
 * first tap, which flips Safari into a playback session that ignores the silent switch.
 *
 * Adapted from feross/unmute-ios-audio (MIT). Must be called from inside a `pointerdown`
 * (or other trusted user-gesture) handler — never from arbitrary code.
 *
 * CRITICAL: silentAudio.play() MUST be the first thing that runs — no `await` may
 * precede it, or the gesture token is spent and iOS rejects the play() (the historic
 * cause of the "tap to begin does nothing" flakiness). The real AudioContext resume
 * is handled by Tone.start(), which the caller fires on the same gesture in parallel.
 *
 * Never imports Tone or three; pure DOM only.
 */

export async function unmuteIOSAudio(silentAudio: HTMLAudioElement): Promise<void> {
  // Kick the silent MP3 element synchronously inside the gesture. This is the
  // actual mute-switch workaround. Returning the play() promise lets the caller
  // await completion, but the call itself happens on the gesture stack.
  silentAudio.muted = false;
  // The MP3 file itself is silent. Keep the element volume audible so iOS
  // treats it as a real media playback session for hardware mute unlock.
  silentAudio.volume = 1;
  try {
    await silentAudio.play();
  } catch (err) {
    throw new Error(
      `unmuteIOSAudio: silent.mp3 failed to play: ${(err as Error).message}`,
    );
  }
}
